import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { KeyRound, Loader2, ShieldAlert, Check } from 'lucide-react';
import { toast } from 'sonner';
import { changePassword } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { getErrorMessage } from '../../lib/errors';

export function PasswordChangeCard() {
  const { user, refresh } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const mustReset = !!user?.must_reset_password;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('New password must be different from your current password.');
      return;
    }
    setBusy(true);
    try {
      await changePassword({ currentPassword, newPassword });
      toast.success('Password updated. Please log in again next time using your new password.');
      setDone(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // Refresh the user context so must_reset_password clears the banner
      try { await refresh?.(); } catch (_) { /* non-fatal */ }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not change password'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-border" data-testid="settings-password-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound size={18} className="text-primary" />
          <h2 className="text-lg font-semibold">Security &middot; Password</h2>
        </div>

        {mustReset && !done && (
          <Alert
            className="border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800"
            data-testid="settings-password-reset-nudge"
          >
            <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-500" />
            <AlertDescription className="text-sm">
              Your account was created with a <b>temporary password</b> after your Razorpay purchase.
              For your security, please set a new password below.
            </AlertDescription>
          </Alert>
        )}

        {done && (
          <Alert className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800">
            <Check className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-sm">
              Password updated successfully.
            </AlertDescription>
          </Alert>
        )}

        <form className="space-y-3" onSubmit={submit} autoComplete="off">
          <div className="space-y-1.5">
            <Label htmlFor="current-password" className="text-sm">Current password</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={busy}
              data-testid="settings-password-current"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-password" className="text-sm">New password</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={busy}
                data-testid="settings-password-new"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password" className="text-sm">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                minLength={6}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={busy}
                data-testid="settings-password-confirm"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Minimum 6 characters. Choose something you don't reuse elsewhere.
          </p>
          <div className="pt-1">
            <Button
              type="submit"
              disabled={busy || !currentPassword || !newPassword || !confirmPassword}
              data-testid="settings-password-submit"
            >
              {busy ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating…</>
              ) : (
                <>Update password</>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default PasswordChangeCard;
